import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov, provTap } from "@/lib/provenance";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { MapPin, Loader2 } from "lucide-react";

// Single, fast "Move to sector" — relocate a whole donor truck OR a movable container into one of
// the fixed yard sectors (type="zone"), picked from a dropdown (faster + more accurate than scanning
// a sun-glared tag, camera-free). Donor moves get a one-tap plain-English confirm (with a holding-area
// warning) before committing — folding in the old scan-dialog's safety without the scan step.
// Donor: only site_location_id changes (donor_id NEVER touched; installed parts follow automatically).
// Container: the whole container and everything in it move together.
export function YardSectorMove({ kind, donorId, containerScan, onDone, testid = "yard-move" }) {
  const [zones, setZones] = useState([]);
  const [zone, setZone] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { prompt, holding } — donor confirm step

  useEffect(() => {
    api.get("/locations").then(({ data }) => setZones(data.filter((l) => l.type === "zone"))).catch(() => {});
  }, []);

  const zObj = zones.find((x) => x.short_code === zone);

  const moveContainer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/containers/move", { container_scan: containerScan, location_scan: zone,
                                                            provenance: provTap("yard_sector") });
      if (data.ok) {
        scanAccept(`${data.container?.short_code || containerScan} → ${data.destination?.short_code || zone}`);
        toast.success(`Moved ${data.container?.short_code || containerScan} → ${data.destination?.short_code || zone} · ${data.moved_parts ?? 0} part(s)`);
        setZone(""); onDone && onDone();
      } else { scanRefuse(data.message || "Move failed"); toast.error(data.message || "Move failed"); }
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Step 1 (donor): ask the backend for a plain-English confirm + holding flag.
  const start = async () => {
    if (!zone) return;
    if (kind !== "donor") return moveContainer();
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${donorId}/place`, { location_scan: zone, confirm: false });
      if (data.needs_confirm) setConfirm({ prompt: data.prompt, holding: data.destination?.holding });
      else { scanRefuse(data.message || "That sector didn't resolve."); toast.error(data.message || "That sector didn't resolve."); }
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Step 2 (donor): commit.
  const commitDonor = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/donors/${donorId}/place`, { location_scan: zone, confirm: true,
                                                                    provenance: provTap("yard_sector") });
      if (data.ok) {
        scanAccept(`TRUCK → ${zObj?.name || zone}`);
        toast.success(`Truck moved → ${zObj?.name || zone}` + (data.destination?.holding ? " — reads as PENDING placement (holding area)." : ""));
        setZone(""); setConfirm(null); onDone && onDone();
      } else { scanRefuse(data.message || "Move failed"); toast.error(data.message || "Move failed"); }
    } catch (e) { scanRefuse(formatApiErrorDetail(e.response?.data?.detail)); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`${testid}-wrap`}>
      <Select value={zone} onValueChange={setZone}>
        <SelectTrigger data-testid={`${testid}-select`} className="rounded-lg h-9 w-[210px] font-mono text-xs">
          <SelectValue placeholder="Move to sector…" />
        </SelectTrigger>
        <SelectContent>
          {zones.map((z) => (
            <SelectItem key={z.id} value={z.short_code} data-testid={`${testid}-opt-${z.short_code}`}>
              {z.short_code} · {z.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button data-testid={`${testid}-go`} onClick={start} disabled={!zone || busy}
        className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-9">
        {busy && !confirm ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MapPin className="w-4 h-4 mr-1" />Move</>}
      </Button>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle>Confirm sector move</DialogTitle>
            <DialogDescription data-testid={`${testid}-confirm-prompt`}>{confirm?.prompt}</DialogDescription>
          </DialogHeader>
          {confirm?.holding && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-700">
              This is a holding area — the truck will read as PENDING placement.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-300" onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
            <Button data-testid={`${testid}-confirm`} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714]" onClick={commitDonor} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MapPin className="w-4 h-4 mr-1.5" />}Confirm move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
